/* Reading a number the way a person actually typed it.
 *
 * `Number('169,5689')` is NaN, and a Polish keyboard puts a comma under the
 * thumb. The figures that get typed into this app are copied off a broker card
 * or an exchange card, so both separators turn up — sometimes in the same
 * string, when a pasted MEXC figure carries a grouping comma and a decimal
 * point. Every field takes both and means the same thing by them.
 *
 * Which separator is the decimal point:
 *
 *   - Both present: the later one divides, the earlier one groups. `1,035.7765`
 *     and `1.035,7765` are the same figure.
 *   - One present, once: it divides. `169,5689` is 169.5689.
 *   - One present, more than once: it cannot be a decimal point, so it groups
 *     throughout. `1.035.776` is one million.
 *
 * Whatever is left then has to *be* a number. A grouped integer must be grouped
 * in threes, and nothing but digits may remain — `1,2,3.4.5` is refused rather
 * than shaken into 12345, because a field that quietly makes a figure out of
 * nonsense is how a wrong number gets saved looking deliberate.
 *
 * That leaves exactly one ambiguous shape: a lone comma with three digits after
 * it, `1,035`, which is 1.035 to a Polish typist and 1035 to a pasted American
 * figure. It reads as the decimal, because typing is what these fields are for;
 * a pasted figure nearly always carries its decimals and so lands in the
 * two-separator case above. The forms show the parsed figure back while you
 * type, so a misread is visible before it is saved.
 */

/** Digits, optionally grouped in threes by `sep`. */
function integerOk(part: string, sep: string): boolean {
  if (part === '') return true;                 // ',5' is half
  if (/^\d+$/.test(part)) return true;
  const grouped = new RegExp(`^\\d{1,3}(${sep === '.' ? '\\.' : ','}\\d{3})+$`);
  return grouped.test(part);
}

/** NaN when the text holds no number this app can use — falsy, so
    `decimal(x) || 0` keeps working where a blank field should read as zero. */
export function decimal(text: string): number {
  const cleaned = text.replace(/[\s   ']/g, '');
  if (cleaned === '') return NaN;

  const sign = cleaned[0] === '-' ? -1 : 1;
  const body = /^[+-]/.test(cleaned) ? cleaned.slice(1) : cleaned;
  if (body === '') return NaN;

  const dots = (body.match(/\./g) ?? []).length;
  const commas = (body.match(/,/g) ?? []).length;

  /* Which one divides. A separator seen more than once is grouping by
     elimination, and one seen once alongside another loses to whichever comes
     later. */
  let point: '.' | ',' | null = null;
  if (dots === 1 && commas === 1) point = body.indexOf('.') > body.indexOf(',') ? '.' : ',';
  else if (dots === 1 && commas === 0) point = '.';
  else if (commas === 1 && dots === 0) point = ',';
  else if (dots === 1 && commas > 1) point = '.';
  else if (commas === 1 && dots > 1) point = ',';
  else if (dots > 1 && commas > 1) return NaN;   // neither can divide, so this is not a figure

  const cut = point === null ? -1 : body.lastIndexOf(point);
  const whole = cut < 0 ? body : body.slice(0, cut);
  const frac = cut < 0 ? '' : body.slice(cut + 1);

  /* Whatever the decimal point is not. With no decimal point at all it is the
     one that repeated, which is how it came to be grouping in the first place. */
  const grouping: '.' | ',' = point === '.' ? ',' : point === ',' ? '.' : commas > 1 ? ',' : '.';
  if (!integerOk(whole, grouping)) return NaN;
  if (!/^\d*$/.test(frac)) return NaN;
  if (whole === '' && frac === '') return NaN;

  const value = Number(`${whole.replace(/[.,]/g, '') || '0'}.${frac || '0'}`);
  return Number.isFinite(value) ? sign * value : NaN;
}

/** The same, with a fallback — for the many fields where a blank or a nonsense
    entry should read as nothing rather than break the sum around it. */
export function decimalOr(text: string, fallback = 0): number {
  const n = decimal(text);
  return Number.isFinite(n) ? n : fallback;
}

/** True when the text is a figure this app can use: finite and above zero.
    Share counts, prices, margins and leverages are all of that kind. */
export function isPositiveDecimal(text: string): boolean {
  const n = decimal(text);
  return Number.isFinite(n) && n > 0;
}
